import { FormField } from '@dev-spendesk/grapes';
import { type PayableVersion } from '@spendesk/bookkeeping-core-types';
import React, { useState } from 'react';

import { AutocompleteNoOptions } from 'src/core/common/components/AutocompleteNoOptions';
import { AutocompleteSearch } from 'src/core/common/components/AutocompleteSearch';
import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { AutomationConflictSelector } from '../../../../../../payable/components/AutomationConflictSelector';
import { AutomationIcon } from '../../../../../../payable/components/AutomationIcon';
import { PayableRuleAutomationExistsCallout } from '../../../../../../payable/components/PayableRuleAutomationExistsCallout/PayableRuleAutomationExistsCallout';
import { shouldPayableRuleAutomationExistsCalloutBeDisplayed } from '../../../../../../payable/components/PayableRuleAutomationExistsCallout/shouldPayableRuleAutomationExistsCalloutBeDisplayed';
import { type Payable } from '../../../PayablePanelContainer';

type CostCenter = {
  id: string;
  name: string;
};

type Props = {
  payable: Payable;
  costCenters: CostCenter[];
  costCenter: CostCenter | undefined;
  error: string | undefined;
  onChange: (costCenter: CostCenter) => void;
};

export const PayableAccountingSectionCostCenter = ({
  payable,
  costCenters,
  costCenter: currentCostCenter,
  error,
  onChange,
}: Props) => {
  const { t } = useTranslation('global');

  const [hasFixedRuleConflict, setHasFixedRuleConflict] = useState(false);

  const maybeUnappliedCostCenter =
    payable.automation?.analyticalFieldAssociations?.find(
      (afa) => afa.fieldKind === 'costCenter' && !afa.isAppliedOnPayable,
    );

  const maybeAutomation = payable.automation?.analyticalFieldAssociations?.find(
    (afa) => afa.fieldKind === 'costCenter' && afa.isAppliedOnPayable,
  );

  const currentOrAutomated =
    currentCostCenter ??
    (maybeAutomation
      ? costCenters.find(
          (costCenter) => costCenter.id === maybeAutomation.fieldEntityValueId,
        )
      : undefined);

  const isCurrentlyAutomatedValue = !!maybeAutomation;

  const visibleRuleBasedAutomation =
    isCurrentlyAutomatedValue &&
    currentCostCenter &&
    maybeAutomation?.kind === 'ruleBased' &&
    maybeAutomation.output === currentCostCenter.id
      ? maybeAutomation
      : undefined;

  return (
    <FormField
      className="PayableAccountingSectionCostCenter"
      label={t('payables.panel.costCenter')}
      alertMessage={error}
    >
      <AutocompleteSearch
        fit="parent"
        options={costCenters.map((costCenter) => ({
          key: costCenter.id,
          label: costCenter.name,
        }))}
        value={
          currentOrAutomated
            ? { key: currentOrAutomated.id, label: currentOrAutomated.name }
            : undefined
        }
        renderPrefix={() =>
          isCurrentlyAutomatedValue && (
            <AutomationIcon
              automation={maybeAutomation}
              {...(maybeAutomation?.kind === 'ruleBased' && {
                rule: maybeAutomation.rule,
              })}
              t={t}
            />
          )
        }
        renderNoOptions={(rawValue) => (
          <AutocompleteNoOptions value={rawValue} />
        )}
        onSelect={(costCenterOption) => {
          onChange(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            costCenters.find(
              (costCenter) => costCenter.id === costCenterOption?.key,
            )!,
          );
        }}
      />
      {maybeUnappliedCostCenter &&
        maybeUnappliedCostCenter.kind === 'ruleBased' &&
        currentCostCenter &&
        shouldPayableRuleAutomationExistsCalloutBeDisplayed({
          payable: {
            version: payable.version as PayableVersion,
          },
          currentValue: currentCostCenter.id,
          ruleBasedAutomation: maybeUnappliedCostCenter,
        }) && (
          <PayableRuleAutomationExistsCallout<'costCenter'>
            payableIds={[payable.id]}
            currentValue={currentCostCenter.name || ''}
            payableRule={maybeUnappliedCostCenter.rule}
            onUpdate={(output) => {
              const nextCostCenter = costCenters.find((cc) => cc.id === output);
              if (nextCostCenter) {
                onChange(nextCostCenter);
              }
            }}
            translateOutputValue={(output) =>
              output instanceof Object ? output.name : output
            }
            appliedOnProperty="costCenter"
            ruleConflict={maybeUnappliedCostCenter?.conflict}
            {...(maybeUnappliedCostCenter.conflict && {
              conflictSelectorCalloutProps: {
                ruleConflict: maybeUnappliedCostCenter.conflict,
                selectedRule: maybeUnappliedCostCenter.rule,
                onSelect: (output) => {
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  const costCenter = costCenters.find(
                    (cc) => cc.id === output,
                  )!;
                  onChange(costCenter);
                },
              },
            })}
          />
        )}
      {visibleRuleBasedAutomation?.conflict && !hasFixedRuleConflict && (
        <div className="col-span-2 -mt-8">
          <AutomationConflictSelector
            selectedRule={visibleRuleBasedAutomation.rule}
            ruleConflict={visibleRuleBasedAutomation.conflict}
            onSelect={(output) => {
              setHasFixedRuleConflict(true);
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              const costCenter = costCenters.find((cc) => cc.id === output)!;
              onChange(costCenter);
            }}
          />
        </div>
      )}
    </FormField>
  );
};
